import { useEffect, useState } from "react";
import Tealium from "@4tn/webx-analytics/lib/Tealium";
import TopAndTabBarContainer from "@common/TopAndTabBarContainer";
import PageContainer from "@pageContent/common/PageContainer";
import { ScreenSizes } from "@constants/consts";
import useScreenSize from "@utils/common/useScreenSize";
import * as Styled from "./SearchModal.styled";
import SearchModalResult from "./components/SearchModalResult";
import SearchModalTabBar from "./components/SearchModalTabBar";
import SearchModalTopBar from "./components/SearchModalTopBar";
import useSearchValues from "./useSearchValues";

const SearchModal: React.FC = () => {
  const { isModalOpen, searchValue, tabValue } = useSearchValues();
  const screenSize = useScreenSize();

  const [hasResults, setHasResults] = useState(false);
  const [shouldAnimateTabBar, setShouldAnimateTabBar] = useState(false);

  useEffect(() => {
    setShouldAnimateTabBar((prevState) => {
      if (screenSize === ScreenSizes.xs || screenSize === ScreenSizes.sm || screenSize === ScreenSizes.md) return false;
      if (isModalOpen && prevState && hasResults) return false;
      if (!isModalOpen && !prevState) return true;
      return prevState;
    });
  }, [isModalOpen, screenSize, hasResults]);

  useEffect(() => {
    if (!isModalOpen) {
      Tealium.setDataLayer({ search_keyword: null, search_results: null });
    }
    document.documentElement.style.setProperty("overflow-y", isModalOpen ? "hidden" : "auto"); // Prevents background scrolling when the modal is open
  }, [isModalOpen]);

  useEffect(() => {
    const onSearchResults = (data: WebXEventParams["searchResults"]) => setHasResults(data.hasResults);
    window.eventBus.on("searchResults", onSearchResults);

    return () => window.eventBus.off("searchResults", onSearchResults);
  }, []);

  if (!isModalOpen) {
    return null;
  }

  const searchTermHasResults = !!(searchValue && (hasResults || tabValue));

  return (
    <Styled.SearchModalContainer>
      <TopAndTabBarContainer>
        <SearchModalTopBar />
        {(searchTermHasResults || shouldAnimateTabBar) && (
          <SearchModalTabBar shouldAnimateTabBar={shouldAnimateTabBar} searchTermHasResults={searchTermHasResults} />
        )}
      </TopAndTabBarContainer>
      <PageContainer>
        <SearchModalResult />
      </PageContainer>
    </Styled.SearchModalContainer>
  );
};

export default SearchModal;
